import { Link } from 'react-router-dom';
import type { Unit } from '../../hooks/useDashboardData';
import { UnitType } from '../../types/index';
import { computeWaterDepthCm, computeTankVolumeLiters, computePercentFilled, waterReadingGapLabel } from '@shared/metrics';
import './TankRow.css';

interface TankRowProps {
  unit: Unit;
}

function formatTimeAgo(dateString: string): string {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 0) return 'Just now'; // Handle clock drift

  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

export function TankRow({ unit }: TankRowProps) {
  const isMobileStation = unit.unitType === UnitType.MobileStation;

  // Water volume calculations (shared metric math — single source of truth).
  // unit.currentLevel is the raw measurement in mm.
  let volumeLiters: number | null = null;
  let percentFilled: number | null = null;

  if (isMobileStation) {
    const depthCm = computeWaterDepthCm(unit.currentLevel, unit);
    if (depthCm !== null) {
      const v = computeTankVolumeLiters(depthCm, unit);
      const p = computePercentFilled(depthCm, unit);
      volumeLiters = v === null ? null : Math.round(v);
      percentFilled = p === null ? null : Math.round(p);
    }
  }

  // Charging vs discharging from two consecutive voltage readings. Without a
  // previous reading the trend is unknown — show nothing rather than fabricating
  // one from the time of day (D4).
  let voltageTrend: { arrow: string; label: string } | null = null;
  if (unit.previousVoltage !== undefined && unit.previousVoltage !== 0) {
    voltageTrend = unit.currentVoltage > unit.previousVoltage
      ? { arrow: '↑', label: 'Charging' }
      : { arrow: '↓', label: 'Discharging' };
  }

  // Voltage color thresholds
  let voltageColorClass = 'text-good';
  if (unit.currentVoltage < 3.2) {
    voltageColorClass = 'text-critical';
  } else if (unit.currentVoltage < 3.5) {
    voltageColorClass = 'text-warning';
  }

  return (
    <div className="tank-row">
      {/* Name and ID Column, plus Station Type next to name */}
      <div className="tank-row-col col-name">
        <div className="unit-name-container">
          <Link to={`/dashboard/units/${unit.unitId}`} className="hyperlink unit-title">
            {unit.nodeName}
          </Link>
          <span className="type-badge-static">
            {isMobileStation ? 'Mobile Station' : 'Repeater'}
          </span>
        </div>
        <span className="unit-id-badge">ID: {unit.nodeId}</span>
      </div>

      {/* Measurement/Level Column */}
      <div className="tank-row-col col-level">
        {isMobileStation && volumeLiters !== null && percentFilled !== null ? (
          <div className="volume-display">
            <span className="volume-liters-text">{volumeLiters.toLocaleString()} L</span>
            <span className="volume-percent-text">
              ({percentFilled}%)
            </span>
          </div>
        ) : isMobileStation ? (
          <span className="level-placeholder">{waterReadingGapLabel(unit, unit.currentLevel)}</span>
        ) : (
          <span className="level-placeholder">—</span>
        )}
      </div>

      {/* Battery Voltage Column */}
      <div className="tank-row-col col-voltage">
        <span className={`voltage-text ${voltageColorClass}`}>
          {unit.currentVoltage.toFixed(2)} V
          {voltageTrend && <span title={voltageTrend.label}> {voltageTrend.arrow}</span>}
        </span>
      </div>

      {/* Last Updated Column */}
      <div className="tank-row-col col-updated">
        <span className="updated-text">
          {formatTimeAgo(unit.lastSeenAt)}
        </span>
      </div>

      {/* Action Link Column */}
      <div className="tank-row-col col-action">
        <Link to={`/dashboard/units/${unit.unitId}`} className="view-details-btn">
          View Details →
        </Link>
      </div>
    </div>
  );
}
